import { createTheme } from '@mui/material/styles';

const getTheme = (mode) => {
  const isDark = mode === 'dark';

  return createTheme({
    palette: {
      mode,
      primary: {
        main: isDark ? '#4d8fcc' : '#003366',
        light: isDark ? '#7fb3e0' : '#1a4d7a',
        dark: isDark ? '#2c5f8f' : '#002244',
        contrastText: '#ffffff',
      },
      secondary: {
        main: isDark ? '#ffb74d' : '#ff9800',
        light: isDark ? '#ffd180' : '#ffb74d',
        dark: isDark ? '#f57c00' : '#f57c00',
        contrastText: isDark ? '#000000' : '#ffffff',
      },
      background: {
        default: isDark ? '#121212' : '#f5f5f5',
        paper: isDark ? '#1e1e1e' : '#ffffff',
      },
      text: {
        primary: isDark ? 'rgba(255, 255, 255, 0.87)' : '#2c3e50',
        secondary: isDark ? 'rgba(255, 255, 255, 0.60)' : '#546e7a',
        disabled: isDark ? 'rgba(255, 255, 255, 0.38)' : 'rgba(0, 0, 0, 0.38)',
      },
      divider: isDark ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.12)',
      success: {
        main: isDark ? '#66bb6a' : '#4caf50',
        light: isDark ? '#81c784' : '#81c784',
        dark: isDark ? '#388e3c' : '#388e3c',
      },
      warning: {
        main: isDark ? '#ffa726' : '#ff9800',
        light: isDark ? '#ffb74d' : '#ffb74d',
        dark: isDark ? '#f57c00' : '#f57c00',
      },
      error: {
        main: isDark ? '#f44336' : '#f44336',
        light: isDark ? '#e57373' : '#e57373',
        dark: isDark ? '#d32f2f' : '#d32f2f',
      },
      info: {
        main: isDark ? '#29b6f6' : '#2196f3',
        light: isDark ? '#4fc3f7' : '#64b5f6',
        dark: isDark ? '#0288d1' : '#1976d2',
      },
      action: {
        active: isDark ? 'rgba(255, 255, 255, 0.56)' : 'rgba(0, 0, 0, 0.54)',
        hover: isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.04)',
        selected: isDark ? 'rgba(255, 255, 255, 0.16)' : 'rgba(0, 0, 0, 0.08)',
        disabled: isDark ? 'rgba(255, 255, 255, 0.26)' : 'rgba(0, 0, 0, 0.26)',
        disabledBackground: isDark ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.12)',
      },
    },
    typography: {
      fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
      h1: {
        fontFamily: '"Playfair Display", serif',
        fontWeight: 700,
      },
      h2: {
        fontFamily: '"Playfair Display", serif',
        fontWeight: 700,
      },
      h3: {
        fontFamily: '"Playfair Display", serif',
        fontWeight: 700,
      },
      h4: {
        fontFamily: '"Playfair Display", serif',
        fontWeight: 700,
      },
      h5: {
        fontFamily: '"Playfair Display", serif',
        fontWeight: 700,
      },
      h6: {
        fontFamily: '"Playfair Display", serif',
        fontWeight: 700,
      },
    },
    shape: {
      borderRadius: 8,
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            scrollbarColor: isDark ? '#6b6b6b #2b2b2b' : '#c1c1c1 #f1f1f1',
            '&::-webkit-scrollbar, & *::-webkit-scrollbar': {
              width: '12px',
              height: '12px',
            },
            '&::-webkit-scrollbar-thumb, & *::-webkit-scrollbar-thumb': {
              borderRadius: '8px',
              backgroundColor: isDark ? '#6b6b6b' : '#c1c1c1',
              minHeight: '24px',
              border: `3px solid ${isDark ? '#2b2b2b' : '#f1f1f1'}`,
            },
            '&::-webkit-scrollbar-thumb:hover, & *::-webkit-scrollbar-thumb:hover': {
              backgroundColor: isDark ? '#959595' : '#a8a8a8',
            },
            '&::-webkit-scrollbar-track, & *::-webkit-scrollbar-track': {
              backgroundColor: isDark ? '#2b2b2b' : '#f1f1f1',
            },
            transition: 'background-color 0.3s ease, color 0.3s ease',
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            fontWeight: 500,
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            boxShadow: isDark
              ? '0 2px 8px rgba(0,0,0,0.4)'
              : '0 2px 8px rgba(0,0,0,0.1)',
            backgroundImage: 'none',
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
          },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            backgroundImage: 'none',
            borderRight: isDark ? '1px solid rgba(255, 255, 255, 0.12)' : '1px solid rgba(0, 0, 0, 0.12)',
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            borderBottom: isDark ? '1px solid rgba(255, 255, 255, 0.12)' : '1px solid rgba(0, 0, 0, 0.12)',
          },
          head: {
            fontWeight: 600,
            backgroundColor: isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)',
          },
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              '& fieldset': {
                borderColor: isDark ? 'rgba(255, 255, 255, 0.23)' : 'rgba(0, 0, 0, 0.23)',
              },
            },
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            fontWeight: 500,
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            backgroundImage: 'none',
          },
        },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            backgroundColor: isDark ? 'rgba(97, 97, 97, 0.95)' : 'rgba(97, 97, 97, 0.92)',
            fontSize: '0.75rem',
          },
        },
      },
      MuiListItemButton: {
        styleOverrides: {
          root: {
            '&.Mui-selected': {
              backgroundColor: isDark ? 'rgba(77, 143, 204, 0.16)' : 'rgba(0, 51, 102, 0.08)',
              '&:hover': {
                backgroundColor: isDark ? 'rgba(77, 143, 204, 0.24)' : 'rgba(0, 51, 102, 0.12)',
              },
            },
          },
        },
      },
    },
  });
};

export default getTheme;
